import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../contexts/ProfileContext';
import { useStreamAuth } from '../hooks/useStreamAuth';
import { APP_CONFIG } from '../config/AppConfig';

// Product rule: profiles always show the BKFC knuckle icon — never backend
// profile thumbnails, Google account pictures, or letter initials.
const PROFILE_AVATAR = '/assets/images/knuckle.png';

const ProfileDropdown = () => {
  const navigate = useNavigate();
  const { logout, user } = useStreamAuth();
  const { profiles, currentProfile, setActiveProfile } = useProfile();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // The Spotlight backend names the primary profile "Owner" by default. Show the
  // user's first name (or email-username) instead so the label feels personal.
  const getProfileDisplayName = profile => {
    const name = profile?.profile_name;
    if (name && name !== 'Owner') {
      return name;
    }
    if (user?.firstName) {
      return user.firstName;
    }
    if (user?.first_name) {
      return user.first_name;
    }
    const email = user?.email;
    if (email) {
      const local = email.split('@')[0];
      return local.charAt(0).toUpperCase() + local.slice(1);
    }
    return name || '';
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle profile switching
  const handleProfileSwitch = async profile => {
    const success = await setActiveProfile(profile);
    if (success) {
      setIsOpen(false);
      // No page reload needed - profile context will update automatically
    }
  };

  // Handle settings navigation
  const handleSettings = () => {
    setIsOpen(false);
    navigate('/settings');
  };

  // Handle logout
  const handleLogout = () => {
    setIsOpen(false);
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  // Don't render if no current profile
  if (!currentProfile) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 transition-colors rounded-lg hover:bg-white/10"
        aria-label="Profile menu"
      >
        {/* Profile Avatar */}
        <div className="flex-shrink-0 w-8 h-8 overflow-hidden bg-black/40 rounded-full">
          <img
            src={PROFILE_AVATAR}
            alt={getProfileDisplayName(currentProfile)}
            className="object-contain w-full h-full p-1.5"
          />
        </div>

        {/* Profile Name */}
        <span className="hidden text-sm font-medium text-white sm:block">
          {getProfileDisplayName(currentProfile)}
        </span>

        {/* Dropdown Arrow */}
        <svg
          className={`w-4 h-4 text-white transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 z-50 w-64 mt-2 border border-gray-700 rounded-lg shadow-xl top-full bg-secondary">
          {/* Current Profile Header */}
          <div className="px-4 py-3 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 overflow-hidden bg-black/40 rounded-full">
                <img
                  src={PROFILE_AVATAR}
                  alt={getProfileDisplayName(currentProfile)}
                  className="object-contain w-full h-full p-1.5"
                />
              </div>
              <div>
                <div className="font-medium text-white">
                  {getProfileDisplayName(currentProfile)}
                </div>
              </div>
            </div>
          </div>

          {/* Other Profiles */}
          {profiles.length > 1 && (
            <div className="py-2">
              <div className="px-4 py-2 text-xs font-medium tracking-wide uppercase text-white/60">
                Switch to
              </div>
              {profiles
                .filter(profile => profile._id !== currentProfile._id)
                .map(profile => (
                  <button
                    key={profile._id}
                    onClick={() => handleProfileSwitch(profile)}
                    className="flex items-center w-full gap-3 px-4 py-2 text-left transition-colors hover:bg-white/10"
                  >
                    <div className="flex-shrink-0 w-8 h-8 overflow-hidden bg-black/40 rounded-full">
                      <img
                        src={PROFILE_AVATAR}
                        alt={getProfileDisplayName(profile)}
                        className="object-contain w-full h-full p-1.5"
                      />
                    </div>
                    <span className="text-sm text-white">{getProfileDisplayName(profile)}</span>
                  </button>
                ))}
            </div>
          )}

          {/* Actions */}
          <div className="py-2 border-t border-gray-700">
            <button
              onClick={handleSettings}
              className="flex items-center w-full gap-3 px-4 py-2 text-left transition-colors hover:bg-white/10"
            >
              <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 bg-gray-600 rounded-full">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <span className="text-sm text-white">Settings</span>
            </button>

            <a
              href={APP_CONFIG.SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
              className="flex items-center w-full gap-3 px-4 py-2 text-left transition-colors hover:bg-white/10"
            >
              <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 bg-gray-600 rounded-full">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <span className="text-sm text-white">Help &amp; Support</span>
            </a>

            <div className="pt-2 mt-2 border-t border-gray-700">
              <button
                onClick={handleLogout}
                className="flex items-center w-full gap-3 px-4 py-2 text-left transition-colors hover:bg-white/10"
              >
                <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 bg-gray-600 rounded-full">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                </div>
                <span className="text-sm text-white">Log Out</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
